/* eslint-disable no-unused-vars */
import React, {
  useState,
  useCallback,
  useEffect,
  useContext,
  useRef
} from 'react';
import { Context } from 'components/Store';
import LoadingSpinner from 'components/LoadingSpinner';
import UnsavedChangesModal from 'components/UnsavedChangesModal';
import useUnsavedChangesGuard from 'hooks/useUnsavedChangesGuard';

import { useToast } from 'components/ToastContainer';
import useCropData from 'hooks/useCropData';
import usePropertyFilters from 'hooks/usePropertyFilters';
import {
  LAST_COORDINATES_STORED,
  PROPERTY_VIEW_MODE
} from 'screens/Property/helpers/constants';
import {
  MASS_ASSIGNER_SUCCESS_COUNTER,
  RESET_MASS_ASSIGNER,
  SET_IS_SORT_BY_CROPS
} from 'reducers/reducer';
import createAction from 'helpers/createAction';
import PropertiesTree from './PropertiesTree';
import PropertyLandingMap from './PropertyLandingMap';
import PropertyTable from './PropertyTable';
import CreatePropertyModal from './CreatePropertyModal';
import PropertyTopHeader from './PropertyTopHeader/index';
import ImportPropertyModal from './ImportPropertyModal';
import EditFarm from './EditFarm';
import PropertyBreadcrumb from './components/PropertyBreadcrumb';
import MassAssignerHeader from './components/MassAssignerHeader';
import PropertyContributionMargin from './PropertyContributionMargin/PropertyContributionMargin';
import PropertyCostAnalysis from './PropertyCostAnalysis';
import PropertyLandingContext, { PropertyLandingProvider } from './context';
import './index.css';

const fftWidth = '338px';

const PropertiesLanding = () => {
  return (
    <PropertyLandingProvider>
      <PropertiesLandingContent />
    </PropertyLandingProvider>
  );
};

const PropertiesLandingContent = () => {
  const {
    params,
    // conditions
    isCostAnalysisViewEnabled,
    isPropertyDetailsEnabled,
    isHugeOrganization,
    isLoadingProperties,
    //
    selectedProperty,
    viewMode,
    onViewModeChange,
    onDrawFieldsClick,
    // map data
    isLoadingFields,
    propertyLandingPageData,
    fieldsAndAreasGeoJSONCollection,
    setGeoJSONCollectionFeatures,
    filterFeatureCollectionByParentId,
    fetchProperties,
    isFormDirty,
    setFormDirty,
    isNavigationBlocked,
    guardNavigation,
    confirmNavigation,
    cancelNavigation,
    enableEdit,
    setEnableEdit
  } = useContext(PropertyLandingContext);

  const [
    {
      isGlobalCropSeasonLoading,
      isFieldImportedSuccessful,
      isLoadingOrg,
      isMassAssignerActive,
      fieldsToMassiveAssign,
      massAssignerSuccessCounter,
      isExportPDFMapsActive,
      importedFields,
      loggedInUserOrgPermission,
      organization
    },
    dispatch
  ] = useContext(Context);

  const { crops } = useCropData();
  const {
    filteredProperties,
    setPropertiesToFilter,
    showArchivedProperties
  } = usePropertyFilters(undefined, isHugeOrganization);
  const toast = useToast();
  const [localSelectedProperty, setLocalSelectedProperty] = useState([
    filteredProperties
  ]);
  // Cancel has nothing to do beyond leaving the form, and the guard closes the
  // editor itself on both the clean and the discard path.
  const onRequestExitEdit = useCallback(() => guardNavigation(), [
    guardNavigation
  ]);

  // Covers leaving the screen entirely (side navigation, browser unload).
  // In-page navigation - property tree selection and the Map/Details/Cost
  // Analysis switch - never reaches the router, so it is guarded in the
  // Property Landing context instead.
  useUnsavedChangesGuard({
    isDirty: isFormDirty,
    isSaving: false,
    showDiscardModal: guardNavigation
  });
  const [selectedCrop, setSelectedCrop] = useState();
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(
    !!isFieldImportedSuccessful
  );

  const prevSelectedPropertyIdRef = useRef(selectedProperty?.id);

  PropertiesLanding.zoomPropertyOut = () => {
    setGeoJSONCollectionFeatures(
      filterFeatureCollectionByParentId(
        fieldsAndAreasGeoJSONCollection,
        selectedProperty.id,
        fieldsToMassiveAssign
      )
    );
  };

  const reloadProperties = () => {
    if (isGlobalCropSeasonLoading || !organization?.id) return;
    if (isHugeOrganization) {
      fetchProperties(
        { seasonIds: JSON.parse(localStorage.getItem('selectedCropSeasons')) },
        params
      );
    } else {
      fetchProperties(
        { seasonIds: JSON.parse(localStorage.getItem('selectedCropSeasons')) },
        { ...params, includeArchived: showArchivedProperties }
      );
    }
  };

  const formPanel = {
    Farm: (
      <EditFarm
        farmName={selectedProperty.name}
        farmId={selectedProperty.id}
        reloadFarmData={reloadProperties}
        setView={onViewModeChange}
      />
    )
  };

  const onUploadFileMode = () => setShowImportModal(prev => !prev);
  const openCreateModalFn = useCallback(
    () => guardNavigation(() => setOpenCreateModal(true)),
    [guardNavigation]
  );

  useEffect(() => {
    // When selectedProperty.id changes via a tree/map click, onPropertySelection
    // already updated the GeoJSON layers. Only re-run when mass-assigner state changes.
    const propertyIdChanged =
      selectedProperty?.id !== prevSelectedPropertyIdRef.current;
    prevSelectedPropertyIdRef.current = selectedProperty?.id;
    if (propertyIdChanged && !isMassAssignerActive) return;
    PropertiesLanding.zoomPropertyOut();
  }, [selectedProperty.id, isMassAssignerActive, fieldsToMassiveAssign]);

  const displayHeader = !selectedProperty?.isEditing && !isExportPDFMapsActive;

  const fetchPropertiesCallback = useCallback(() => {
    fetchProperties(
      { seasonIds: JSON.parse(localStorage.getItem('selectedCropSeasons')) },
      { ...params, includeArchived: showArchivedProperties }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization?.id, isGlobalCropSeasonLoading, showArchivedProperties]);

  useEffect(() => {
    if (isHugeOrganization) return;
    if (isGlobalCropSeasonLoading || !organization?.id) return;
    fetchPropertiesCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPropertiesCallback]);

  useEffect(() => {
    if (!isHugeOrganization) return;
    if (isGlobalCropSeasonLoading || !organization?.id) return;
    fetchProperties(
      { seasonIds: JSON.parse(localStorage.getItem('selectedCropSeasons')) },
      params
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, organization?.id, isGlobalCropSeasonLoading]);

  useEffect(() => {
    return () => {
      dispatch({ type: RESET_MASS_ASSIGNER });
      localStorage.removeItem(LAST_COORDINATES_STORED);
    };
  }, [dispatch]);

  useEffect(() => {
    setPropertiesToFilter(propertyLandingPageData?.properties);
    setLocalSelectedProperty(filteredProperties);
  }, [
    propertyLandingPageData?.properties,
    filteredProperties,
    setPropertiesToFilter
  ]);

  useEffect(() => {
    setLocalSelectedProperty(filteredProperties);
  }, [filteredProperties]);

  useEffect(() => {
    if (isExportPDFMapsActive === true) {
      return;
    }
    createAction(dispatch, SET_IS_SORT_BY_CROPS, false);
  }, [isExportPDFMapsActive, dispatch]);

  useEffect(() => {
    if (Object.keys(massAssignerSuccessCounter).length) {
      const {
        toastType,
        toastTitle,
        toastDescription
      } = massAssignerSuccessCounter;

      const adjustedToastType = toastType === 'warning' ? 'warn' : toastType;
      toast[adjustedToastType](toastTitle, { content: toastDescription });
      setTimeout(() => {
        dispatch({
          type: MASS_ASSIGNER_SUCCESS_COUNTER,
          payload: {}
        });
      }, 5000);

      if (isHugeOrganization) {
        fetchProperties(
          {
            seasonIds: JSON.parse(localStorage.getItem('selectedCropSeasons'))
          },
          params
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [massAssignerSuccessCounter]);

  return (
    <>
      <div className="w-full h-full overflow-hidden">
        <PropertyBreadcrumb
          // Disabled as it is not working; it will be fixed in CWFWEB-7792
          disabledPropertyChange
        />

        {openCreateModal && (
          <CreatePropertyModal
            open={openCreateModal}
            closeModal={setOpenCreateModal}
            drawFieldsClick={() =>
              onDrawFieldsClick(selectedProperty, filteredProperties)
            }
            uploadFilesClick={onUploadFileMode}
            selectedProperty={selectedProperty}
          />
        )}

        {isLoadingOrg || isGlobalCropSeasonLoading || isLoadingProperties ? (
          <LoadingSpinner
            message=""
            label="Loading properties"
            className="w-full h-full"
          />
        ) : (
          <div className="w-full h-full">
            {isMassAssignerActive && (
              <MassAssignerHeader reloadData={fetchProperties} />
            )}
            <div
              className={`flex flex-row w-full ${
                isMassAssignerActive ? '-h-24' : 'h-full'
              }`}
            >
              <div className="h-full" style={{ width: fftWidth }}>
                {selectedProperty?.isEditing ? (
                  formPanel[selectedProperty.type]
                ) : (
                  <PropertiesTree
                    openCreateModal={openCreateModalFn}
                    properties={filteredProperties}
                    rawProperties={propertyLandingPageData?.properties ?? []}
                    memberRole={loggedInUserOrgPermission?.role}
                    reloadProperties={reloadProperties}
                  />
                )}
              </div>
              <div
                className="h-full relative flex flex-col"
                style={{ width: `calc(100% - ${fftWidth})` }}
              >
                {displayHeader && (
                  <div
                    className={
                      viewMode !== PROPERTY_VIEW_MODE.MAP
                        ? 'w-full'
                        : 'absolute z-40 bg-white w-full'
                    }
                  >
                    <PropertyTopHeader
                      viewMode={viewMode}
                      onRequestExitEdit={onRequestExitEdit}
                      enableEdit={enableEdit}
                      properties={filteredProperties}
                      selectedCrop={selectedCrop}
                      setSelectedCrop={setSelectedCrop}
                    />
                  </div>
                )}

                {isCostAnalysisViewEnabled &&
                  viewMode === PROPERTY_VIEW_MODE.COST && (
                    <PropertyCostAnalysis selectedCrop={selectedCrop} />
                  )}

                {viewMode === PROPERTY_VIEW_MODE.MAP && (
                  <div className="relative h-full">
                    {displayHeader &&
                      !isCostAnalysisViewEnabled &&
                      isPropertyDetailsEnabled && (
                        <PropertyContributionMargin />
                      )}
                    <PropertyLandingMap
                      properties={localSelectedProperty}
                      fieldsLoading={isLoadingFields}
                      propertyCount={organization.properties?.propertyCount}
                      crops={crops}
                    />
                  </div>
                )}
                {viewMode === PROPERTY_VIEW_MODE.INFO && (
                  <PropertyTable
                    onRequestExitEdit={onRequestExitEdit}
                    edited={isFormDirty}
                    setEdited={setFormDirty}
                    enableEdit={enableEdit}
                    setEnableEdit={setEnableEdit}
                  />
                )}
              </div>
              <ImportPropertyModal
                open={showImportModal}
                close={onUploadFileMode}
                reload={reloadProperties}
                importedFields={importedFields}
              />
            </div>
          </div>
        )}
        <UnsavedChangesModal
          open={isNavigationBlocked}
          onContinueEditing={cancelNavigation}
          onDiscard={confirmNavigation}
        />
      </div>
    </>
  );
};

export default PropertiesLanding;
